/**
 * Files on tickets: what is accepted and how it is named (spec
 * docs/superpowers/specs/2026-10-22-attachments-design.md). Pure.
 *
 * The content decides the type, not the extension: the first bytes are sniffed and a file whose
 * content does not match its name is refused ("That is not a PNG"). Text types (CSV, TXT) have no
 * magic, so they are accepted when the bytes are text and the extension says so. The allow-list is
 * the one array below; Carlyrose's list edits it (Jeff, 2026-10-22).
 */
export const MAX_BYTES = 25 * 1024 * 1024;
/** Screen recordings (Carlyrose, 2026-10-22): video gets more room. */
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
export function maxBytesFor(mime) {
    return mime.startsWith('video/') ? MAX_VIDEO_BYTES : MAX_BYTES;
}
/** Accepted types, the extensions that may carry each, and the word the desk shows. */
export const ALLOWED = [
    { mime: 'image/png', ext: ['png'], word: 'Image' },
    { mime: 'image/jpeg', ext: ['jpg', 'jpeg'], word: 'Image' },
    { mime: 'image/gif', ext: ['gif'], word: 'Image' },
    { mime: 'image/webp', ext: ['webp'], word: 'Image' },
    { mime: 'image/heic', ext: ['heic', 'heif'], word: 'Image' },
    { mime: 'video/quicktime', ext: ['mov'], word: 'Video' },
    { mime: 'video/mp4', ext: ['mp4', 'm4v'], word: 'Video' },
    { mime: 'video/webm', ext: ['webm'], word: 'Video' },
    { mime: 'application/pdf', ext: ['pdf'], word: 'PDF' },
    { mime: 'text/csv', ext: ['csv'], word: 'CSV' },
    { mime: 'text/plain', ext: ['txt', 'log'], word: 'Text' },
    { mime: 'application/vnd.ms-excel', ext: ['xls'], word: 'Spreadsheet' },
    { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ext: ['xlsx'], word: 'Spreadsheet' },
    { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ext: ['docx'], word: 'Document' },
];
export const ACCEPT_ATTRIBUTE = ALLOWED.flatMap((a) => a.ext.map((e) => `.${e}`)).join(',');
export function typeWord(mime) {
    return ALLOWED.find((a) => a.mime === mime)?.word ?? 'File';
}
export function extensionOf(filename) {
    const m = /\.([a-z0-9]+)$/i.exec(filename.trim());
    return m?.[1]?.toLowerCase() ?? '';
}
const starts = (b, sig, at = 0) => sig.every((v, i) => b[at + i] === v);
const ascii = (b, s, at = 0) => starts(b, Array.from(s, (c) => c.charCodeAt(0)), at);
/**
 * The type the bytes say they are, from the first few hundred bytes. `null` when nothing matched
 * (which for an allowed text extension means "check it is text"). Office files are zips: the
 * extension picks between xlsx and docx, both accepted, so the sniff says `zip`.
 */
export function sniff(head) {
    if (starts(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
        return 'image/png';
    if (starts(head, [0xff, 0xd8, 0xff]))
        return 'image/jpeg';
    if (ascii(head, 'GIF87a') || ascii(head, 'GIF89a'))
        return 'image/gif';
    if (ascii(head, 'RIFF') && ascii(head, 'WEBP', 8))
        return 'image/webp';
    if (ascii(head, 'ftyp', 4) && (ascii(head, 'heic', 8) || ascii(head, 'heix', 8) || ascii(head, 'mif1', 8) || ascii(head, 'heif', 8)))
        return 'image/heic';
    if (ascii(head, 'ftyp', 4) && ascii(head, 'qt', 8))
        return 'video/quicktime';
    if (ascii(head, 'ftyp', 4))
        return 'video/mp4';
    if (starts(head, [0x1a, 0x45, 0xdf, 0xa3]))
        return 'video/webm';
    if (ascii(head, '%PDF'))
        return 'application/pdf';
    if (starts(head, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]))
        return 'application/vnd.ms-excel';
    if (starts(head, [0x50, 0x4b, 0x03, 0x04]))
        return 'zip';
    if (starts(head, [0x4d, 0x5a]))
        return 'application/x-msdownload';
    if (ascii(head, '\x7fELF'))
        return 'application/x-elf';
    return null;
}
/** Bytes that read as text: no NUL, and nothing below 0x20 but tab, newline, return, form feed. */
export function looksLikeText(head) {
    if (head.length === 0)
        return false;
    for (const b of head) {
        if (b === 0)
            return false;
        if (b < 0x20 && b !== 0x09 && b !== 0x0a && b !== 0x0d && b !== 0x0c)
            return false;
    }
    return true;
}
/** The decision for one file from its name, its size and its first bytes. */
export function checkFile(input) {
    const name = input.filename.trim();
    if (input.bytes <= 0)
        return { ok: false, reason: 'The file is empty.' };
    const ext = extensionOf(name);
    const byExt = ALLOWED.find((a) => a.ext.includes(ext));
    if (!byExt)
        return { ok: false, reason: ext ? `.${ext} files are not accepted.` : 'The file needs an extension.' };
    if (input.bytes > maxBytesFor(byExt.mime))
        return { ok: false, reason: byExt.mime.startsWith('video/') ? 'Videos up to 100 MB.' : 'Files up to 25 MB.' };
    const sniffed = sniff(input.head);
    const isOffice = byExt.mime.startsWith('application/vnd.openxmlformats');
    const isText = byExt.mime === 'text/csv' || byExt.mime === 'text/plain';
    if (isOffice) {
        if (sniffed !== 'zip')
            return { ok: false, reason: `That is not a ${byExt.ext[0]?.toUpperCase() ?? ''} file.` };
        return { ok: true, mime: byExt.mime, word: byExt.word };
    }
    if (isText) {
        if (sniffed !== null || !looksLikeText(input.head))
            return { ok: false, reason: `That is not a ${byExt.word.toLowerCase()} file.` };
        return { ok: true, mime: byExt.mime, word: byExt.word };
    }
    // A QuickTime file is also an ISO base media file; a .mov that sniffs as mp4 is still fine, and the reverse.
    const video = byExt.mime.startsWith('video/') && sniffed !== null && sniffed.startsWith('video/') && sniffed !== 'video/webm' && byExt.mime !== 'video/webm';
    if (sniffed !== byExt.mime && !video)
        return { ok: false, reason: `That is not a ${byExt.word === 'Image' ? ext.toUpperCase() : byExt.word}.` };
    return { ok: true, mime: byExt.mime, word: byExt.word };
}
/** ASCII, no path separators, at most 120 characters, the extension kept. */
export function safeName(filename) {
    const ext = extensionOf(filename);
    const stem = filename
        .trim()
        .replace(/\.[a-z0-9]+$/i, '')
        .normalize('NFKD')
        .replace(/[^\x20-\x7e]/g, '')
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^[. ]+/, '')
        .replace(/[. ]+$/, '');
    const base = (stem || 'file').slice(0, 120 - (ext ? ext.length + 1 : 0));
    return ext ? `${base}.${ext}` : base;
}
/** Names unique within one note: "shot.png", "shot-2.png", "shot-3.png". */
export function uniqueNames(names) {
    const seen = new Map();
    return names.map((n) => {
        const key = n.toLowerCase();
        const count = (seen.get(key) ?? 0) + 1;
        seen.set(key, count);
        if (count === 1)
            return n;
        const ext = extensionOf(n);
        const stem = ext ? n.slice(0, -(ext.length + 1)) : n;
        return ext ? `${stem}-${String(count)}.${ext}` : `${stem}-${String(count)}`;
    });
}
export function sizeWord(bytes) {
    if (bytes < 1024)
        return `${String(bytes)} B`;
    if (bytes < 1024 * 1024)
        return `${String(Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
/** The line a Slack post carries for a file it could not attach: "screenshot.png, 212 KB". */
export function attachmentLine(a) {
    return `${a.filename}, ${sizeWord(a.bytes)}`;
}
//# sourceMappingURL=attachments.js.map